import { Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import { PermissionService } from "src/services/permissionService/permissionService";
import { useDispatch } from "src/store";

import { usePolicyContentUpdate } from "@dashboard/policies/hooks/usePolicyContentUpdate/usePolicyContentUpdate";
import { useCurrentWorkspace } from "@dashboard/workspaces/api/helpers";
import { setPaneTab } from "@devices/store";
import { Group } from "@devices/types";
import { FileInfo } from "@files/types";
import { DialogProps, useTheme } from "@mui/material";
import { PrimaryButton } from "@shared/CustomButton";
import { RadioField, RadioFieldGorup } from "@shared/form/RadioField";
import { Modal } from "@shared/Modal/Modal";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";

import { getNoEligibleDevicesTooltipKey, GroupApkTargets } from "../groupApkStatus";
import { installFileOnGroupDevices } from "../installPackage";

const getInstallImmediatelyTooltipKey = (targets: GroupApkTargets) =>
    targets.eligible.length > 0 ? undefined : getNoEligibleDevicesTooltipKey(targets);

type FormValues = {
    action: "installImmediately" | "addToPolicy";
};
type Props = Omit<DialogProps, "onClose"> & {
    group: Group | undefined;
    file: FileInfo;
    targets: GroupApkTargets;
    assignedPolicy: API.PolicyViewModel | undefined;
    isLoadingPolicies: boolean;
    onClose: () => void;
};
export const PickInstallationTypeDialog = (props: Props) => {
    const { group, file, targets, assignedPolicy, isLoadingPolicies, ...modalProps } = props;
    const { t } = useTranslation(["modals", "sidepanels", "common"]);
    const theme = useTheme();
    const dispatch = useDispatch();
    const { data: workspace = null } = useCurrentWorkspace();
    const { workspaceAbility } = PermissionService();

    const cannotUpdatePolicy = workspaceAbility(workspace).cannot("update", "Policy");
    const { updateContent } = usePolicyContentUpdate();

    const otherGroupsCount = assignedPolicy?.groupIds?.filter((id) => id !== group?.id).length ?? 0;

    const canInstallNow = targets.eligible.length > 0;
    const hasDevices = targets.totalDevices > 0;
    const canAddToPolicy = hasDevices && Boolean(assignedPolicy) && !cannotUpdatePolicy;
    const cannotConfirm = !canInstallNow && !canAddToPolicy;
    const installImmediatelyTooltipKey = getInstallImmediatelyTooltipKey(targets);

    const installImmediately = async () => {
        if (!group) {
            return;
        }
        try {
            const result = await installFileOnGroupDevices({
                file,
                groupId: group.id,
                targets,
            });

            if (result.reached) {
                showSuccessToast(t("sidepanels:groupPanel.toast.installQueued", { count: result.reached }));
            }
            if (result.failed) {
                showErrorToast(t("sidepanels:groupPanel.toast.installFailed", { count: result.failed }));
            }
            if (result.usedGroupCommand) {
                dispatch(setPaneTab("COMMANDS"));
            }
        } catch (error) {
            const err = error as Error;
            showErrorToast(err.message);
        }
    };

    const addToPolicy = async () => {
        if (!assignedPolicy) {
            return;
        }
        const manifest = file.metadata?.file?.apk?.manifest;
        const command: API.Policy = {
            command: "installPackage",
            source: "storage",
            file: file.id,
            ...(manifest?.package ? { package: manifest.package } : {}),
            ...(manifest?.versioncode ? { version: manifest.versioncode } : {}),
        };

        const fallbackContent = assignedPolicy.content ?? [];
        const updated = await updateContent({
            id: assignedPolicy.id,
            name: assignedPolicy.name,
            content: [...fallbackContent, command],
        });
        if (updated) {
            dispatch(setPaneTab("POLICY"));
        }
    };

    const onSubmit = async (values: FormValues) => {
        switch (values.action) {
            case "installImmediately":
                await installImmediately();
                break;
            case "addToPolicy":
                await addToPolicy();
                break;
            default:
                break;
        }
        props.onClose();
    };

    const getAddToPolicyHelperText = () => {
        if (isLoadingPolicies) {
            return t("modals:installationType.input.addToPolicy.helperText");
        }
        if (!group?.policyId) {
            return t("modals:installationType.input.addToPolicy.helperTextNoPolicy");
        }
        if (otherGroupsCount > 0) {
            return t("modals:installationType.input.addToPolicy.helperTextShared", { count: otherGroupsCount });
        }
        return t("modals:installationType.input.addToPolicy.helperText");
    };

    const getAddToPolicyTooltip = () => {
        if (cannotUpdatePolicy) {
            return t("common:tooltip.cannotManagePolicies");
        }
        if (!hasDevices) {
            return t("modals:installationType.tooltip.noDevices");
        }
        if (!isLoadingPolicies && !group?.policyId) {
            return t("modals:installationType.tooltip.noPolicyAssigned");
        }
        return "";
    };

    return (
        <Form<FormValues>
            onSubmit={onSubmit}
            initialValues={{ action: !canInstallNow && canAddToPolicy ? "addToPolicy" : "installImmediately" }}
            render={({ handleSubmit, submitting, form }) => (
                <form onSubmit={handleSubmit}>
                    <Modal
                        title={t("modals:installationType.title")}
                        endButton={
                            <PrimaryButton onClick={form.submit} disabled={submitting || cannotConfirm} loading={submitting}>
                                {t("common:button.install")}
                            </PrimaryButton>
                        }
                        {...modalProps}
                    >
                        <RadioFieldGorup name="action">
                            <RadioField
                                label={t("modals:installationType.input.installImmediately.caption")}
                                value="installImmediately"
                                helperText={t("modals:installationType.input.installImmediately.helperText")}
                                style={{ marginBottom: theme.spacing(5) }}
                                disabled={!canInstallNow}
                                tooltip={installImmediatelyTooltipKey ? t(installImmediatelyTooltipKey) : ""}
                            />

                            <RadioField
                                label={t("modals:installationType.input.addToPolicy.caption")}
                                value="addToPolicy"
                                helperText={getAddToPolicyHelperText()}
                                style={{ marginBottom: theme.spacing(4) }}
                                disabled={!canAddToPolicy}
                                tooltip={getAddToPolicyTooltip()}
                            />
                        </RadioFieldGorup>
                    </Modal>
                </form>
            )}
        />
    );
};
